import { api, type CatalogItem, type NowNext, type YoutubeItem } from '../api.ts';
import { el, mount, onTap } from '../ui/dom.ts';
import { iconEl } from '../ui/icons.ts';
import { appMain, ensureAppShell, pageIntro, setSearchValue, shelfHead } from '../ui/shell.ts';
import { errorNote, loadingInto, skeletonHero, skeletons, stateBlock, toast } from '../ui/feedback.ts';
import {
  channelRow, continueCard, episodeRow, favoriteCard, posterCard, youtubeCard,
  type ContinueEntry,
} from '../ui/cards.ts';
import { navigate, renderGuard } from '../router.ts';
import { hasIptv } from '../state.ts';
import { isCar } from '../ui/surface.ts';
import { play } from '../playback.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { withSpaceKey } from '../ui/space-key.ts';

const PAGE = 12;

// Hazır kanalların kategorisi veride Türkçe adla durur (süzme ve sıra o adla
// yapılır); ekranda seçili dildeki karşılığı gösterilir.
const CATEGORY_KEYS: Record<string, MessageKey> = {
  Ulusal: 'cat.national', Haber: 'cat.news', Spor: 'cat.sports', Belgesel: 'cat.documentary',
  'Müzik': 'cat.music', 'Çocuk': 'cat.kids', 'Diğer': 'common.other',
};

function categoryLabel(raw: string | undefined): string {
  if (!raw) return '';
  const key = CATEGORY_KEYS[raw];
  return key ? t(key) : raw;
}
const DEMO_HERO = '/demo/coast.svg';
/** Plus kilidi: ürün ekranı örneği + kısa açıklama + CTA. Fiyat burada yazmaz:
 *  tek kaynağı Paketler ekranıdır (ikinci kopya eskiyip yanlış fiyat gösterirdi).
 *  Gerçek kullanıcı kaynağı çekilmez, ücretsiz önizleme oynatılmaz. */
function iptvLocked(what: string): HTMLElement {
  return el('div', {},
    el('div', { class: 'app-hero', style: { marginBottom: '28px' } },
      el('div', { class: 'scene' },
        el('img', { src: DEMO_HERO, alt: t('locked.alt') }),
        el('div', { class: 'scene-copy' },
          el('span', { class: 'tag' }, t('locked.tag')),
          el('h2', {}, t('locked.title1'), el('br'), t('locked.title2')),
          el('p', {}, t('locked.body', { what })),
        ),
      ),
    ),
    stateBlock({
      title: t('locked.stateTitle'),
      body: t('locked.stateBody'),
      action: { label: t('common.viewPlans'), onClick: () => navigate('/plans') },
    }),
  );
}

function withWrap(...children: Array<Node | null>): HTMLElement {
  return el('div', { class: 'wrap' }, ...children);
}

// ================================== Keşfet ==================================

export async function discoverView(): Promise<void> {
  ensureAppShell();
  const host = appMain();
  setSearchValue('');

  mount(host, el('div', { class: 'wrap discover-page' },
    pageIntro(t('discover.title'), null, t('discover.subtitle'), { search: true }),
    el('div', { id: 'hero' }, skeletonHero()),
    el('div', { id: 'continue' }),
    el('div', { id: 'topics' }, el('div', { class: 'topic-loading', 'aria-hidden': 'true' },
      ...Array.from({ length: 6 }, () => el('span', { class: 'sk' })))),
    el('div', { id: 'discover' }, shelfHead(t('discover.shelf')), skeletons(4)),
    el('div', { id: 'live-strip' }),
  ));

  void renderContinue(host.querySelector('#continue') as HTMLElement);
  void renderDiscover(host.querySelector('#hero') as HTMLElement, host.querySelector('#discover') as HTMLElement, host.querySelector('#topics') as HTMLElement);
  void renderLiveStrip(host.querySelector('#live-strip') as HTMLElement);
}

async function renderContinue(host: HTMLElement): Promise<void> {
  try {
    const data = await api.get<{ items: ContinueEntry[] }>('/api/library/continue');
    if (!data.items.length) { mount(host, ''); return; }
    mount(host,
      shelfHead(t('discover.continue'), t('discover.goList'), () => navigate('/library')),
      el('div', { class: 'rail' }, ...data.items.slice(0, 10).map((item) => continueCard(item))),
    );
  } catch {
    mount(host, '');
  }
}

/** Oynatıcı kapanınca yalnız "devam et" rafı tazelenir. Ekranın tamamını yeniden
 *  çizmek kaydırma konumunu, seçili kategoriyi ve yüklenmiş sayfaları siliyordu. */
export function refreshContinueShelf(): void {
  const host = document.getElementById('continue');
  if (host && host.isConnected) void renderContinue(host);
}

async function renderDiscover(heroHost: HTMLElement, gridHost: HTMLElement, topicHost: HTMLElement): Promise<void> {
  let topic = '';
  let offset = 0;
  let loading = false;
  let first: YoutubeItem | null = null;

  const loadPage = async (reset: boolean) => {
    if (loading) return;
    loading = true;
    if (reset) {
      offset = 0;
      mount(heroHost, skeletonHero());
      mount(gridHost, shelfHead(t('discover.shelf')), skeletons(4));
    }
    try {
      const data = await api.get<{ items: YoutubeItem[]; nextOffset: number | null; total: number }>(
        `/api/catalog/youtube/discover?offset=${offset}&limit=${PAGE}${topic ? `&topic=${topic}` : ''}`,
      );
      if (reset) {
        mount(gridHost, shelfHead(t('discover.shelf')), el('div', { class: 'media-grid', id: 'discover-grid' }),
          el('div', { id: 'discover-more', style: { marginTop: '20px' } }));
        // Hero: gerçek bir içerik varsa onu kullan; yoksa uydurma trend koyma.
        first = data.items[0] ?? null;
        renderHero(heroHost, first);
      }
      const grid = gridHost.querySelector('#discover-grid') as HTMLElement;
      const more = gridHost.querySelector('#discover-more') as HTMLElement;
      const items = reset ? data.items.slice(1) : data.items;
      if (!data.items.length && offset === 0) {
        // Raf başlığı yok: altında raf olmayınca iki başlık üst üste okunuyordu.
        mount(gridHost, stateBlock({
          title: t('discover.emptyTitle'),
          body: t('discover.emptyBody'),
          action: { label: t('common.retry'), onClick: () => void loadPage(true) },
        }));
        return;
      }
      for (const item of items) grid.appendChild(youtubeCard(item));
      if (data.nextOffset !== null) {
        offset = data.nextOffset;
        const button = el('button', { class: 'btn btn-outline', type: 'button' }, t('common.loadMore'));
        onTap(button, () => void loadPage(false));
        mount(more, button);
      } else {
        mount(more, '');
      }
    } catch (err) {
      if (reset) {
        mount(gridHost, stateBlock({
          title: t('discover.loadFail'),
          body: (err as Error).message,
          action: { label: t('common.retry'), onClick: () => void loadPage(true) },
        }));
        renderHero(heroHost, null);
      } else {
        toast(t('discover.moreFail'), 'error');
      }
    } finally {
      loading = false;
    }
  };

  // Topics are optional; a slow topics request must not hold up the catalog.
  void loadPage(true);
  try {
    const data = await api.get<{ topics: Array<{ key: string; label: string }> }>('/api/catalog/youtube/topics');
    const all = [{ key: '', label: t('discover.mixed') }, ...data.topics];
    mount(topicHost, el('div', { class: 'chip-row scroll-x' },
      ...all.map((item) => {
        const chip = el('button', { class: 'chip' + (item.key === topic ? ' active' : ''), type: 'button', 'aria-pressed': String(item.key === topic) }, item.label);
        onTap(chip, () => {
          if (loading || topic === item.key) return;
          topic = item.key;
          topicHost.querySelectorAll('.chip').forEach((n) => { n.classList.remove('active'); n.setAttribute('aria-pressed', 'false'); });
          chip.classList.add('active');
          chip.setAttribute('aria-pressed', 'true');
          void loadPage(true);
        });
        return chip;
      })));
  } catch { mount(topicHost, ''); }
}

/** Hero yalnız gerçek bir içerikle kurulur; içerik yoksa bölüm hiç açılmaz. */
function renderHero(host: HTMLElement, item: YoutubeItem | null): void {
  if (!item) { mount(host, ''); return; }
  const watch = el('button', { class: 'btn btn-light', type: 'button' }, iconEl('play'), t('discover.watch'));
  onTap(watch, () => void play({
    source: { type: 'youtube', videoId: item.id, live: item.live, title: item.title, channel: item.channel, durationSec: item.duration },
    title: item.title,
    subtitle: item.channel,
    relatedQuery: { exclude: item.id, channel: item.channel, title: item.title },
  }));
  mount(host, el('div', { class: 'app-hero' },
    el('div', { class: 'scene' },
      el('img', { src: item.thumbnail, alt: '' }),
      el('div', { class: 'scene-copy' },
        el('span', { class: 'tag' }, item.live ? t('discover.liveNow') : item.channel || t('nav.discover')),
        el('h2', { class: 'clamp2' }, item.title),
        el('div', { class: 'scene-actions' }, watch),
      ),
    ),
  ));
}

async function renderLiveStrip(host: HTMLElement): Promise<void> {
  try {
    const data = await api.get<{ channels: Array<{ id: string; number?: number; name: string; favorite: boolean; category?: string }> }>('/api/catalog/tv/channels');
    if (!data.channels.length) { mount(host, ''); return; }
    const list = el('div', { class: 'channel-list' },
      ...data.channels.slice(0, 9).map((channel) => channelRow({
        itemKey: `tv:${channel.id}`, kind: 'tv', title: channel.name,
        mark: String(channel.number ?? 0).padStart(2, '0'),
        favorite: channel.favorite, meta: { channelId: channel.id },
        onPlay: () => void play({
          source: { type: 'tv', channelId: channel.id },
          title: channel.name,
          channelList: data.channels.map((c) => ({
            id: c.id, name: c.name, target: { source: { type: 'tv' as const, channelId: c.id }, title: c.name },
          })),
        }),
      })));
    mount(host, shelfHead(t('discover.liveStrip'), t('discover.allChannels'), () => navigate('/live')), list);
  } catch {
    mount(host, '');
  }
}

// =================================== Arama ==================================

export async function searchView(_params: Record<string, string>, query: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const term = query.get('q') ?? '';
  setSearchValue(term);

  if (!term) {
    // Araçta yazmak zor: son aramalar tek dokunuşla tekrarlanabilsin.
    const recent = recentSearches();
    const field = el('input', {
      type: 'search', placeholder: t('search.placeholder'),
      'aria-label': t('nav.search'), autocomplete: 'off', enterkeyhint: 'search',
    });
    field.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key !== 'Enter') return;
      const value = field.value.trim();
      if (value) navigate(`/search?q=${encodeURIComponent(value)}`);
    });
    mount(host, withWrap(
      pageIntro(t('search.title')),
      el('div', { class: 'field', style: { maxWidth: '520px' } }, withSpaceKey(field)),
      recent.length
        ? el('div', {},
            shelfHead(t('search.recent'), t('search.clear'), () => { clearSearches(); void searchView(_params, query); }),
            el('div', { class: 'chip-row' }, ...recent.map((item) => {
              const chip = el('button', { class: 'chip', type: 'button' }, item);
              onTap(chip, () => navigate(`/search?q=${encodeURIComponent(item)}`));
              return chip;
            })))
        : stateBlock({
            title: t('search.emptyTitle'),
            body: t('search.emptyBody'),
            action: { label: t('common.goDiscover'), onClick: () => navigate('/browse') },
          }),
    ));
    field.focus();
    return;
  }
  rememberSearch(term);

  const countLabel = el('div', { class: 'sub', id: 'count' }, t('search.searching'));
  mount(host, withWrap(
    pageIntro(`“${term}”`, null),
    countLabel,
    el('div', { id: 'filters' }),
    el('div', { id: 'results', style: { marginTop: '20px' } }, skeletons(8)),
    el('div', { id: 'more', style: { marginTop: '24px' } }),
  ));

  const resultsHost = host.querySelector('#results') as HTMLElement;
  const moreHost = host.querySelector('#more') as HTMLElement;
  const filterHost = host.querySelector('#filters') as HTMLElement;

  let scope: 'video' | 'iptv' = 'video';
  let offset = 0;
  let loading = false;

  const filters = [
    { key: 'video' as const, label: t('search.videos') },
    { key: 'iptv' as const, label: t('search.mylist') },
  ];
  mount(filterHost, el('div', { class: 'chip-row' }, ...filters.map((f) => {
    // Yetki yoksa çip kilitli görünür: tıklayınca paketlere gidiyordu ama
    // neden gittiği belli değildi.
    const locked = f.key === 'iptv' && !hasIptv();
    const chip = el('button', {
      class: 'chip' + (f.key === scope ? ' active' : '') + (locked ? ' locked' : ''),
      type: 'button',
      'aria-label': locked ? t('nav.lockedLabel', { label: f.label }) : undefined,
    }, f.label, locked ? el('span', { class: 'chip-lock' }, iconEl('lock')) : null);
    onTap(chip, () => {
      if (locked) { navigate('/plans'); return; }
      scope = f.key;
      filterHost.querySelectorAll('.chip').forEach((n) => n.classList.remove('active'));
      chip.classList.add('active');
      void load(true);
    });
    return chip;
  })));

  // İstek sırası: kapsam çipi yükleme sürerken değişirse yeni istek eskisinin
  // yerini alır (eskiden çip "Listem"i gösterirken video sonuçları kalıyordu);
  // "daha fazla"ya çift dokunuş ise ikinci isteği başlatmaz.
  let loadSeq = 0;
  const load = async (reset: boolean) => {
    if (loading && !reset) return;
    const mine = ++loadSeq;
    loading = true;
    if (reset) { offset = 0; loadingInto(resultsHost, 8); mount(moreHost, ''); }
    try {
      if (scope === 'iptv') {
        const data = await api.get<{ items: CatalogItem[] }>(`/api/catalog/iptv-search?q=${encodeURIComponent(term)}`);
        if (mine !== loadSeq) return;
        countLabel.textContent = t('search.countMine', { n: data.items.length });
        if (!data.items.length) {
          mount(resultsHost, stateBlock({
            title: t('search.noMatchTitle'),
            body: t('search.noMatchBody'),
            action: { label: t('common.goSettings'), onClick: () => navigate('/settings') },
          }));
          return;
        }
        mount(resultsHost, el('div', { class: 'poster-grid' },
          ...data.items.map((item) => posterCard(item, { onOpen: (s) => navigate(`/series/${s.id}`) }))));
        return;
      }

      const data = await api.get<{ items: YoutubeItem[]; nextOffset: number | null; total: number }>(
        `/api/catalog/youtube/search?q=${encodeURIComponent(term)}&offset=${offset}&limit=${PAGE}`,
      );
      if (mine !== loadSeq) return;
      countLabel.textContent = t('search.count', { n: data.total });
      if (reset) mount(resultsHost, el('div', { class: 'media-grid', id: 'result-grid' }));
      const grid = resultsHost.querySelector('#result-grid') as HTMLElement;
      if (!data.items.length && offset === 0) {
        mount(resultsHost, stateBlock({
          title: t('search.noResultTitle'),
          body: t('search.noResultBody'),
        }));
        return;
      }
      for (const item of data.items) grid.appendChild(youtubeCard(item));
      if (data.nextOffset !== null) {
        offset = data.nextOffset;
        const button = el('button', { class: 'btn btn-outline', type: 'button' }, t('search.loadMoreN', { n: data.total - offset }));
        onTap(button, () => void load(false));
        mount(moreHost, button);
      } else {
        mount(moreHost, el('p', { class: 'inline-note' }, t('search.end')));
      }
    } catch (err) {
      if (mine !== loadSeq) return;
      if (reset) {
        mount(resultsHost, stateBlock({
          title: t('search.fail'),
          body: (err as Error).message,
          action: { label: t('common.retry'), onClick: () => void load(true) },
        }));
      } else {
        mount(moreHost, errorNote(t('discover.moreFail')));
      }
    } finally {
      if (mine === loadSeq) loading = false;
    }
  };

  void load(true);
}

/** Son aramalar yalnız bu tarayıcıda durur; sunucuya gitmez. */
const RECENT_KEY = 'akis.aramalar';

function recentSearches(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string').slice(0, 8) : [];
  } catch {
    return [];
  }
}

function rememberSearch(term: string): void {
  try {
    const list = [term, ...recentSearches().filter((x) => x !== term)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch { /* depolama kapalı */ }
}

function clearSearches(): void {
  try { localStorage.removeItem(RECENT_KEY); } catch { /* depolama kapalı */ }
}

// =================================== Canlı ==================================

export async function liveView(): Promise<void> {
  ensureAppShell();
  const host = appMain();

  // Bu ekranda genel arama YOK: hemen altındaki "Kanal ara" ile karışıyordu ve
  // ilk kanal ancak 700 px aşağıda başlıyordu.
  const search = el('input', { type: 'search', placeholder: t('live.channelSearch'), 'aria-label': t('live.channelSearch'), class: 'channel-search' });
  mount(host, withWrap(
    pageIntro(t('live.title'), null, hasIptv() ? t('live.subIptv') : t('live.subFree')),
    // Araçta klavye yazmak zor; hazır kanallar için kategori şeridi zaten yeterli.
    isCar() ? null : el('div', { class: 'field', style: { maxWidth: '320px' } }, search),
    el('div', { id: 'cat-row' }),
    el('div', { id: 'builtin' }, skeletons(3)),
    el('div', { id: 'iptv-live', style: { marginTop: '8px' } }),
  ));

  let channels: Array<{ id: string; number?: number; name: string; favorite: boolean; category?: string }> = [];
  let category = '';
  const builtinHost = host.querySelector('#builtin') as HTMLElement;
  const catHost = host.querySelector('#cat-row') as HTMLElement;

  const renderBuiltin = (term: string) => {
    const needle = term.toLocaleLowerCase('tr');
    const filtered = channels.filter((c) =>
      (!category || c.category === category)
      && (!needle || c.name.toLocaleLowerCase('tr').includes(needle)));
    const list = el('div', { class: 'channel-list' },
      ...filtered.map((channel) => channelRow({
        itemKey: `tv:${channel.id}`, kind: 'tv', title: channel.name,
        // Numara veriden gelir: kategoriye göre süzünce değişmez.
        mark: String(channel.number ?? 0).padStart(2, '0'), subtitle: categoryLabel(channel.category),
        favorite: channel.favorite, meta: { channelId: channel.id },
        onPlay: () => void play({
          source: { type: 'tv', channelId: channel.id },
          title: channel.name,
          channelList: filtered.map((c) => ({
            id: c.id, name: c.name, target: { source: { type: 'tv' as const, channelId: c.id }, title: c.name },
          })),
        }),
      })));
    mount(builtinHost, shelfHead(t('live.builtin')),
      filtered.length ? list : stateBlock({ title: t('live.noMatch'), body: t('live.noMatchBody') }));
  };

  search.addEventListener('input', () => renderBuiltin(search.value.trim()));

  // Kategori şeridi: kanallar tek listede taranamıyordu. Kategoriler gerçek
  // veriden gelir (config/channels.json), uydurma sayaç yok.
  const paintCategories = () => {
    const counts = new Map<string, number>();
    for (const c of channels) {
      const key = c.category || t('common.other');
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    if (counts.size < 2) { mount(catHost, ''); return; }
    // Sabit, anlamlı bir sıra; listede olmayan kategoriler sona alfabetik eklenir.
    const ORDER = ['Ulusal', 'Haber', 'Spor', 'Belgesel', 'Müzik', 'Çocuk', 'Diğer'];
    const rank = (k: string) => { const i = ORDER.indexOf(k); return i === -1 ? ORDER.length : i; };
    const sorted = [...counts.entries()].sort((a, b) =>
      rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0], 'tr'));
    const entries: Array<[string, number]> = [['', channels.length], ...sorted];
    mount(catHost, el('div', { class: 'chip-row scroll-x' }, ...entries.map(([key, count]) => {
      const chip = el('button', { class: 'chip' + (key === category ? ' active' : ''), type: 'button' },
        key ? categoryLabel(key) : t('common.all'), el('small', {}, String(count)));
      onTap(chip, () => {
        category = key;
        catHost.querySelectorAll('.chip').forEach((n) => n.classList.remove('active'));
        chip.classList.add('active');
        renderBuiltin(search.value.trim());
      });
      return chip;
    })));
  };

  try {
    const data = await api.get<{ channels: Array<{ id: string; number?: number; name: string; favorite: boolean; category?: string }> }>('/api/catalog/tv/channels');
    channels = data.channels;
    paintCategories();
    renderBuiltin('');
  } catch {
    mount(builtinHost, shelfHead(t('live.builtin')),
      stateBlock({ title: t('live.listFail'), body: t('live.listFailBody') }));
  }

  const iptvHost = host.querySelector('#iptv-live') as HTMLElement;
  if (!hasIptv()) {
    mount(iptvHost, shelfHead(t('live.mine')),
      stateBlock({
        title: t('live.mineLockedTitle'),
        body: t('live.mineLockedBody'),
        action: { label: t('live.seePlus'), onClick: () => navigate('/plans') },
      }));
    return;
  }
  mount(iptvHost, shelfHead(t('live.mine'), t('live.goIptv'), () => navigate('/iptv')));
  const holder = el('div', {});
  iptvHost.appendChild(holder);
  await renderIptvLive(holder, 12);
}

// ==================================== IPTV ==================================

type IptvTab = 'channels' | 'movies' | 'series';

export async function iptvView(_params: Record<string, string>, query: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const tab = (query.get('section') as IptvTab) || 'channels';

  if (!hasIptv()) {
    mount(host, withWrap(pageIntro(t('iptv.title')), iptvLocked(t('iptv.lockedWhat'))));
    return;
  }

  const body = el('div', { id: 'iptv-body', style: { marginTop: '24px' } });
  const tabDefs: Array<[IptvTab, string]> = [
    ['channels', t('iptv.tabChannels')], ['movies', t('iptv.tabMovies')], ['series', t('iptv.tabSeries')],
  ];
  const tabs = el('div', { class: 'subnav' },
    ...tabDefs.map(([key, label]) => {
      const button = el('button', { type: 'button', class: key === tab ? 'active' : '' }, label);
      onTap(button, () => navigate(`/iptv?section=${key}`));
      return button;
    }),
  );

  const sync = el('button', { class: 'btn btn-outline btn-sm', type: 'button' }, iconEl('refresh'), t('iptv.sync'));
  onTap(sync, () => void syncAll(sync));

  mount(host, withWrap(pageIntro(t('live.mine'), sync), tabs, body));

  if (tab === 'channels') await renderIptvLive(body, 100, true);
  else if (tab === 'movies') await renderCatalogGrid(body, 'movie');
  else await renderCatalogGrid(body, 'series');
}

async function syncAll(button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  try {
    const sources = await api.get<{ sources: Array<{ id: string; name: string }> }>('/api/sources');
    if (!sources.sources.length) { toast(t('iptv.addFirst'), 'info'); navigate('/settings'); return; }
    for (const source of sources.sources) {
      await api.post(`/api/sources/${source.id}/sync`);
    }
    toast(t('sources.synced'), 'ok');
    location.reload();
  } catch (err) {
    // Yenileme başarısız olsa da çalışan eski katalog silinmez.
    toast(t('sources.syncFail', { error: (err as Error).message }), 'error', 7000);
  } finally {
    button.disabled = false;
  }
}

async function renderIptvLive(host: HTMLElement, limit: number, paged = false): Promise<void> {
  mount(host, skeletons(3));
  let categories: Array<{ category: string; count: number }> = [];
  try {
    const data = await api.get<{ categories: Array<{ category: string; count: number }> }>('/api/catalog/iptv-categories/live');
    categories = data.categories;
  } catch { /* kategori olmadan da liste çalışır */ }

  let category = '';
  const listHost = el('div', {});
  const aside = el('div', { class: 'aside-list' });
  const layout = categories.length > 1
    ? el('div', { class: 'with-aside' }, aside, listHost)
    : listHost;
  mount(host, layout);

  const paint = () => {
    mount(aside, ...[{ category: '', count: 0 }, ...categories].map((item) => {
      const button = el('button', { type: 'button', class: item.category === category ? 'active' : '' },
        el('span', {}, item.category || t('common.all')),
        item.count ? el('span', {}, String(item.count)) : null);
      onTap(button, () => { category = item.category; paint(); void load(true); });
      return button;
    }));
  };

  // Sayfalama: sunucu tek istekte en çok 100 satır verir. Eskiden yalnız ilk
  // sayfa çiziliyordu; büyük bir listede kategorinin geri kalanına arama dışında
  // ulaşılamıyordu. Film/dizi ızgarasındaki "daha fazla" kalıbının aynısı.
  const pageSize = Math.min(limit, 100);
  let offset = 0;
  let all: CatalogItem[] = [];
  let loading = false;
  // İstek sırası: kategori hızlı değiştirilirse geç dönen eski yanıt yenisini ezmesin.
  let loadSeq = 0;
  const moreHost = el('div', { style: { marginTop: '16px' } });

  const rowFor = (item: CatalogItem, epg: Record<string, NowNext>) => channelRow({
    itemKey: item.itemKey, kind: 'iptv-live', title: item.title,
    // Numara yalnız sağlayıcı verdiyse; uydurulmuyor.
    mark: typeof item.extra?.chno === 'string' ? item.extra.chno : undefined,
    logo: item.logo, epg: epg[item.id] ?? null, expectsEpg: !!item.epgChannelId,
    favorite: item.favorite, meta: { itemId: item.id, kind: 'live', logo: item.logo },
    onPlay: () => {
      // Oynatıcının kanal menüsü: yüklenmiş listeden, seçilen kanalın çevresi.
      const index = all.indexOf(item);
      const from = Math.max(0, Math.min(index - 150, all.length - 300));
      void play({
        source: { type: 'iptv', itemId: item.id },
        title: item.title,
        subtitle: item.category,
        channelList: all.slice(from, from + 300).map((c) => ({
          id: c.id, name: c.title, target: { source: { type: 'iptv' as const, itemId: c.id }, title: c.title },
        })),
      });
    },
  });

  const moreButton = (left: number) => {
    const button = el('button', { class: 'btn btn-outline', type: 'button' }, t('search.loadMoreN', { n: left }));
    onTap(button, () => void load(false));
    return button;
  };

  const load = async (reset: boolean) => {
    if (loading && !reset) return;
    const mine = ++loadSeq;
    loading = true;
    if (reset) { offset = 0; all = []; mount(listHost, skeletons(3)); }
    let total = 0;
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: String(pageSize) });
      if (category) params.set('category', category);
      const data = await api.get<{ items: CatalogItem[]; total: number; nextOffset?: number | null }>(`/api/catalog/iptv/live?${params}`);
      if (mine !== loadSeq) return;
      total = data.total;
      if (!data.items.length && offset === 0) {
        mount(listHost, stateBlock({
          title: t('iptv.noChannelTitle'),
          body: t('iptv.noChannelBody'),
          action: { label: t('common.goSettings'), onClick: () => navigate('/settings') },
        }));
        return;
      }
      const epg = await loadEpg(data.items);
      if (mine !== loadSeq) return;
      if (reset) mount(listHost, el('div', { class: 'channel-list' }), moreHost);
      const list = listHost.querySelector('.channel-list') as HTMLElement;
      all = all.concat(data.items);
      for (const item of data.items) list.appendChild(rowFor(item, epg));
      const next = paged && data.items.length ? data.nextOffset ?? null : null;
      if (next !== null) {
        offset = next;
        mount(moreHost, moreButton(Math.max(0, data.total - all.length)));
      } else if (data.total > all.length) {
        mount(moreHost, el('p', { class: 'inline-note' }, t('iptv.showing', { shown: all.length, total: data.total })));
      } else {
        mount(moreHost, '');
      }
    } catch (err) {
      if (mine !== loadSeq) return;
      if (reset) mount(listHost, stateBlock({ title: t('iptv.channelsFail'), body: (err as Error).message }));
      else mount(moreHost, errorNote(t('discover.moreFail')), moreButton(Math.max(0, total - all.length)));
    } finally {
      if (mine === loadSeq) loading = false;
    }
  };

  if (categories.length > 1) paint();
  await load(true);
}

async function loadEpg(items: CatalogItem[]): Promise<Record<string, NowNext>> {
  const withEpg = items.filter((i) => i.epgChannelId).slice(0, 200);
  if (!withEpg.length) return {};
  try {
    const data = await api.post<{ epg: Record<string, NowNext> }>('/api/catalog/iptv-epg', {
      itemIds: withEpg.map((i) => i.id),
    });
    return data.epg;
  } catch {
    return {};
  }
}

async function renderCatalogGrid(host: HTMLElement, kind: 'movie' | 'series'): Promise<void> {
  mount(host, skeletons(6, true));
  let categories: Array<{ category: string; count: number }> = [];
  try {
    const data = await api.get<{ categories: Array<{ category: string; count: number }> }>(`/api/catalog/iptv-categories/${kind}`);
    categories = data.categories;
  } catch { /* kategori zorunlu değil */ }

  let category = '';
  let offset = 0;
  const gridHost = el('div', {});
  const moreHost = el('div', { style: { marginTop: '24px' } });
  const aside = el('div', { class: 'aside-list' });
  const right = el('div', {}, gridHost, moreHost);
  mount(host, categories.length > 1 ? el('div', { class: 'with-aside' }, aside, right) : right);

  const paint = () => {
    mount(aside, ...[{ category: '', count: 0 }, ...categories].map((item) => {
      const button = el('button', { type: 'button', class: item.category === category ? 'active' : '' },
        el('span', {}, item.category || t('common.all')),
        item.count ? el('span', {}, String(item.count)) : null);
      onTap(button, () => { category = item.category; offset = 0; paint(); void load(true); });
      return button;
    }));
  };

  // İstek sırası: kategori hızlı değiştirilirse geç dönen eski yanıt yenisini
  // ezmesin; "daha fazla"ya çift dokunuş aynı sayfayı iki kez eklemesin.
  let loading = false;
  let loadSeq = 0;
  const load = async (reset: boolean) => {
    if (loading && !reset) return;
    const mine = ++loadSeq;
    loading = true;
    if (reset) { offset = 0; mount(gridHost, skeletons(6, true)); mount(moreHost, ''); }
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: '48' });
      if (category) params.set('category', category);
      const data = await api.get<{ items: CatalogItem[]; total: number; nextOffset: number | null }>(
        `/api/catalog/iptv/${kind}?${params}`,
      );
      if (mine !== loadSeq) return;
      if (reset) mount(gridHost, el('div', { class: 'poster-grid', id: 'pg' }));
      const grid = gridHost.querySelector('#pg') as HTMLElement;
      if (!data.items.length && offset === 0) {
        mount(gridHost, stateBlock({
          title: kind === 'movie' ? t('iptv.noMovies') : t('iptv.noSeries'),
          body: t('iptv.noKindBody'),
          action: { label: t('common.goSettings'), onClick: () => navigate('/settings') },
        }));
        return;
      }
      for (const item of data.items) {
        grid.appendChild(posterCard(item, { onOpen: (s) => navigate(`/series/${s.id}`) }));
      }
      if (data.nextOffset !== null) {
        offset = data.nextOffset;
        const button = el('button', { class: 'btn btn-outline', type: 'button' }, t('search.loadMoreN', { n: data.total - offset }));
        onTap(button, () => void load(false));
        mount(moreHost, button);
      } else {
        mount(moreHost, '');
      }
    } catch (err) {
      if (mine !== loadSeq) return;
      mount(gridHost, stateBlock({ title: t('iptv.contentFail'), body: (err as Error).message }));
    } finally {
      if (mine === loadSeq) loading = false;
    }
  };

  if (categories.length > 1) paint();
  await load(true);
}

// ================================ Dizi detayı ===============================

export async function seriesDetailView(params: Record<string, string>): Promise<void> {
  ensureAppShell();
  const host = appMain();
  if (!hasIptv()) { mount(host, withWrap(pageIntro(t('series.title')), iptvLocked(t('series.lockedWhat')))); return; }
  mount(host, withWrap(skeletons(4)));
  // Yanıt gelene dek başka ekrana geçildiyse o ekranı ezme.
  const stale = renderGuard();

  try {
    const data = await api.get<{ item: CatalogItem; episodes?: CatalogItem[] }>(`/api/catalog/iptv-item/${params.id}`);
    if (stale()) return;
    const item = data.item;
    const seasons = new Map<number, CatalogItem[]>();
    for (const episode of data.episodes ?? []) {
      const key = episode.season ?? 1;
      seasons.set(key, [...(seasons.get(key) ?? []), episode]);
    }
    const seasonKeys = [...seasons.keys()].sort((a, b) => a - b);
    const plot = typeof item.extra.plot === 'string' ? item.extra.plot : '';
    const year = item.extra.year ? String(item.extra.year) : '';

    const listHost = el('div', {});
    const chips = el('div', { class: 'chip-row', style: { margin: '24px 0 8px' } });

    const renderSeason = (season: number) => {
      const list = (seasons.get(season) ?? []).sort((a, b) => (a.episode ?? 0) - (b.episode ?? 0));
      mount(listHost, list.length
        ? el('div', {}, ...list.map((episode) => episodeRow(episode, item.title)))
        : stateBlock({ title: t('series.noEpisodesSeason') }));
    };

    mount(chips, ...seasonKeys.map((season, index) => {
      const chip = el('button', { class: 'chip' + (index === 0 ? ' active' : ''), type: 'button' }, t('series.season', { n: season }));
      onTap(chip, () => {
        chips.querySelectorAll('.chip').forEach((n) => n.classList.remove('active'));
        chip.classList.add('active');
        renderSeason(season);
      });
      return chip;
    }));

    const firstEpisode = seasonKeys.length ? (seasons.get(seasonKeys[0]) ?? [])[0] : null;
    const playBtn = firstEpisode
      ? el('button', { class: 'btn btn-light', type: 'button' }, iconEl('play'), t('series.playFirst'))
      : null;
    if (playBtn && firstEpisode) {
      onTap(playBtn, () => void play({
        source: { type: 'iptv', itemId: firstEpisode.id },
        title: `${item.title} — ${firstEpisode.title}`,
      }));
    }

    const back = el('button', { class: 'btn btn-quiet btn-sm', type: 'button' }, iconEl('arrowLeft'), t('series.back'));
    onTap(back, () => navigate('/iptv?section=series'));

    mount(host, withWrap(
      el('div', { class: 'page-intro' }, back),
      // Yatay kapak/arka plan; solda başlık ve oynat.
      el('div', { class: 'app-hero' },
        // Kapak yoksa 2:3 afiş yer tutucusunu 16:9 alana germiyoruz; sade bir
        // yüzey bırakıp yazıyı öne alıyoruz.
        el('div', { class: 'scene' + (item.logo ? '' : ' scene-plain') },
          item.logo ? el('img', { src: item.logo, alt: '' }) : null,
          el('div', { class: 'scene-copy' },
            el('span', { class: 'tag' }, [item.category, year].filter(Boolean).join(' · ') || t('series.tag')),
            el('h2', { class: 'clamp2' }, item.title),
            plot ? el('p', { class: 'clamp2' }, plot) : null,
            playBtn ? el('div', { class: 'scene-actions' }, playBtn) : null,
          ),
        ),
      ),
      seasonKeys.length ? chips : null,
      listHost,
    ));

    if (seasonKeys.length) renderSeason(seasonKeys[0]);
    else mount(listHost, stateBlock({
      title: t('series.noEpisodesTitle'),
      body: t('series.noEpisodesBody'),
    }));
  } catch (err) {
    if (stale()) return;
    mount(host, withWrap(stateBlock({ title: t('series.fail'), body: (err as Error).message })));
  }
}

// =================================== Listem =================================

export async function libraryView(_params: Record<string, string>, query: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const tab = query.get('section') === 'favorites' ? 'favorites' : 'continue';

  const tabDefs: Array<[string, string]> = [['continue', t('library.tabContinue')], ['favorites', t('library.tabFavorites')]];
  const tabs = el('div', { class: 'subnav' },
    ...tabDefs.map(([key, label]) => {
      const button = el('button', { type: 'button', class: key === tab ? 'active' : '' }, label);
      onTap(button, () => navigate(`/library?section=${key}`));
      return button;
    }));

  const body = el('div', { style: { marginTop: '24px' } }, skeletons(4));
  mount(host, withWrap(pageIntro(t('library.title')), tabs, body));

  try {
    if (tab === 'favorites') {
      const data = await api.get<{ favorites: Array<{ itemKey: string; kind: string; title: string; meta: Record<string, any> }> }>(
        '/api/library/favorites',
      );
      mount(body, data.favorites.length
        ? el('div', { class: 'media-grid' }, ...data.favorites.map((fav) => favoriteCard(fav)))
        : stateBlock({
            title: t('library.noFavTitle'),
            body: t('library.noFavBody'),
            action: { label: t('common.goDiscover'), onClick: () => navigate('/browse') },
          }));
    } else {
      const data = await api.get<{ items: ContinueEntry[] }>('/api/library/continue');
      mount(body, data.items.length
        ? el('div', { class: 'media-grid' }, ...data.items.map((item) => continueCard(item)))
        : stateBlock({
            title: t('library.noContinueTitle'),
            body: t('library.noContinueBody'),
            action: { label: t('common.goDiscover'), onClick: () => navigate('/browse') },
          }));
    }
  } catch (err) {
    mount(body, stateBlock({ title: t('library.fail'), body: (err as Error).message }));
  }
}
